import { useEffect } from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { Footer } from "@/components/Footer";
import { trackEvent } from "@/lib/analytics";

const List = ({ items }) => (
    <ul className="list-disc space-y-1.5 pl-5">
        {items.map((item) => (
            <li key={item}>{item}</li>
        ))}
    </ul>
);

const Privacy = () => {
    useEffect(() => {
        document.title = "Privacy Policy | FlowPilot";
        trackEvent("page_view", { page_path: "/privacy" });
    }, []);

    return (
        <>
            <LegalLayout
                title="Privacy Policy"
                updated="October 2026"
                testId="privacy-page"
            >
                <LegalSection title="Introduction" testId="privacy-section-introduction">
                    <p>
                        FlowPilot respects your privacy and is committed to
                        protecting the personal information you share with us.
                    </p>
                </LegalSection>
                <LegalSection title="Information we collect" testId="privacy-section-collection">
                    <p>
                        When you contact FlowPilot, request a Growth Review, or
                        use our website, we may collect information such as:
                    </p>
                    <div className="mt-3">
                        <List
                            items={[
                                "your name",
                                "company name",
                                "email address",
                                "phone number",
                                "website",
                                "business type",
                                "marketing goals",
                                "indicative marketing budget",
                                "information you voluntarily provide in enquiry forms",
                            ]}
                        />
                    </div>
                    <p className="mt-4">
                        We may also collect technical and usage information such
                        as website visits, device information, browser
                        information, referral source and advertising interaction
                        data.
                    </p>
                </LegalSection>
                <LegalSection title="How we use your information" testId="privacy-section-use">
                    <p>We use information to:</p>
                    <div className="mt-3">
                        <List
                            items={[
                                "respond to enquiries",
                                "assess whether FlowPilot may be suitable for your business",
                                "provide requested services",
                                "improve our website, advertising and customer experience",
                                "measure marketing performance",
                                "send relevant follow-up communications",
                                "operate AI-assisted business processes and automations",
                            ]}
                        />
                    </div>
                </LegalSection>
                <LegalSection title="AI and automated processing" testId="privacy-section-ai">
                    <p>
                        FlowPilot may use artificial intelligence and automated
                        systems to help analyse enquiries, draft responses,
                        prioritise leads, generate marketing recommendations and
                        improve service delivery. Important commercial decisions
                        may involve human review where appropriate.
                    </p>
                </LegalSection>
                <LegalSection title="Service providers" testId="privacy-section-providers">
                    <p>
                        We may use trusted third-party technology providers for
                        hosting, automation, analytics, advertising, email,
                        databases and AI processing. These providers may process
                        information on our behalf in accordance with their own
                        security and privacy obligations.
                    </p>
                </LegalSection>
                <LegalSection title="Advertising and analytics" testId="privacy-section-advertising">
                    <p>
                        FlowPilot may use services including Google Ads and
                        analytics tools to understand website performance,
                        advertising effectiveness and customer journeys.
                    </p>
                </LegalSection>
                <LegalSection title="Data retention" testId="privacy-section-retention">
                    <p>
                        We retain personal information only for as long as
                        reasonably necessary for the purposes for which it was
                        collected, including legal, operational and commercial
                        requirements.
                    </p>
                </LegalSection>
                <LegalSection title="Your rights" testId="privacy-section-rights">
                    <p>
                        Depending on applicable law, you may have rights
                        relating to your personal data, including access,
                        correction, deletion, restriction or objection to
                        certain processing.
                    </p>
                </LegalSection>
                <LegalSection title="Contact" testId="privacy-section-contact">
                    <p>For privacy-related enquiries, contact:</p>
                    <p className="mt-2">
                        <a
                            href="mailto:admin@flowpilotops.co.uk"
                            data-testid="privacy-contact-email"
                            className="font-medium text-[#2563EB] hover:text-blue-700"
                        >
                            admin@flowpilotops.co.uk
                        </a>
                    </p>
                </LegalSection>
                <LegalSection title="Changes to this policy" testId="privacy-section-changes">
                    <p>
                        We may update this Privacy Policy from time to time. The
                        latest version will be published on this page.
                    </p>
                </LegalSection>
            </LegalLayout>
            <Footer />
        </>
    );
};

export default Privacy;
